import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { Clock, ShieldCheck, Timer, Play } from 'lucide-react';
import { getQuiz } from '../../services/studentQuizService';
import { formatDateTime } from './utils/utils';
import ConfirmStartQuizModal from '../../components/modals/ConfirmStartQuizModal';
import { Helmet } from "react-helmet-async";

function StartQuizPage() {
  const { quiz_id } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfirmStart, setShowConfirmStart] = useState(false);

  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        const res = await getQuiz(quiz_id);
        setQuiz(res);
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load quiz.');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    if (quiz_id) fetchQuiz();
  }, [quiz_id]);

  const isEnded = useMemo(() => {
    if (!quiz?.end_date) return false;
    return new Date(quiz.end_date) < new Date();
  }, [quiz?.end_date]);

  let pageTitle = "Start Quiz";
  if (quiz?.title) {
    pageTitle = `Start ${quiz.title}`;
  }

  if (loading) return <div className="text-center py-8">Loading quiz...</div>;
  if (!quiz) return <div className="text-center py-8">Quiz not found.</div>;

  return (
    <>
      <Helmet>
        <title>{pageTitle} | ExamPrep</title>
      </Helmet>
      <div className="max-w-2xl mx-auto p-6">
        <h1 className="text-3xl font-bold mb-4">{quiz.topic_detail?.title || quiz.course_detail?.title || 'Quiz'}</h1>
        <p className="text-gray-600 mb-6">
          {quiz.description || 'Please review the quiz details below and click Start when you’re ready.'}
        </p>

        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 mb-6">
          <div className="bg-base-200 p-4 rounded-md flex items-start gap-3">
            <Clock className="w-5 h-5 mt-1 text-info" />
            <div>
              <p className="text-sm text-gray-500">Duration</p>
              <p className="font-medium">{quiz.expected_duration} mins</p>
            </div>
          </div>

          <div className="bg-base-200 p-4 rounded-md flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 mt-1 text-warning" />
            <div>
              <p className="text-sm text-gray-500">Quiz Type</p>
              <p className="font-medium capitalize">{quiz.quiz_type}</p>
            </div>
          </div>

          {quiz.quiz_type === 'exam' && quiz.start_date && (
            <div className="bg-base-200 p-4 rounded-md flex items-start gap-3">
              <Timer className="w-5 h-5 mt-1 text-success" />
              <div>
                <p className="text-sm text-gray-500">Start Time</p>
                <p className="font-medium">{formatDateTime(quiz.start_date)}</p>
              </div>
            </div>
          )}

          {quiz.quiz_type === 'exam' && quiz.end_date && (
            <div className="bg-base-200 p-4 rounded-md flex items-start gap-3">
              <Timer className="w-5 h-5 mt-1 text-error" />
              <div>
                <p className="text-sm text-gray-500">End Time</p>
                <p className="font-medium">{formatDateTime(quiz.end_date)}</p>
              </div>
            </div>
          )}
        </div>

        <div className="text-center mt-8">
          {isEnded ? (
            <button
              className="btn btn-disabled btn-lg gap-2"
            >
              <Play className="w-4 h-4" />
              Quiz Ended
            </button>
          ) : (
            <button
              onClick={() => setShowConfirmStart(true)}
              className="btn btn-primary btn-lg gap-2"
            >
              <Play className="w-4 h-4" />
              Start Quiz
            </button>
          )}
        </div>

        <ConfirmStartQuizModal
          quiz={quiz}
          isOpen={showConfirmStart}
          onClose={() => setShowConfirmStart(false)}
        />
      </div>
    </>
  );
}

export default StartQuizPage;
